import { BellOutlined, DownOutlined, MenuOutlined } from '@ant-design/icons';
import { Badge, Button, Drawer, Dropdown, Layout, Modal } from 'antd';
import ConfirmGlobal from 'components/ConfirmGlobal';
import GoogleAnalyticsTracker from 'components/GoogleAnalyticsTracker';
import LocationHistory from 'components/LocationHistory';
import Navbar from 'components/Navbar';
import Notifications from 'components/Notifications';
import OpenReplayTracker from 'components/OpenReplayTracker';
import RouterScrollToTop from 'components/RouterScrollToTop';
import UserMenu from 'components/UserMenu';
import useOrganization from 'hooks/useOrganization';
import { useSearchParamsMaster } from 'hooks/useSearchParamsMulti';
import { useCallback, useEffect, useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import useLocalStore from 'store/localStore';
import useLoginStore from 'store/loginStore';
import { prefixRelativeOrg } from 'utils/prefixRelativeOrg';
import useRequiredActions from 'hooks/useRequiredActions';
import RequiredActionsIcon from 'components/RequiredActionsIcon';
import RequiredActionsModal from 'components/RequiredActionsModal';
import RoleSelector from 'components/RoleSelector';
import useIsMobile from 'hooks/useIsMobile';
const { Header, Sider } = Layout;

export default function AdminLayout({ mode, navbar = true, ...props }) {
  const { user } = useLoginStore();
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const { organization } = useOrganization();
  const navigate = useNavigate();
  const setOrganizationId = useLocalStore(state => state.setOrganizationId);
  const hasMultipleRoles = useLoginStore(s => s.hasMultipleRoles)();
  const isMobile = useIsMobile();

  useRequiredActions();

  useSearchParamsMaster();

  // Prevent drag & drop of files, except where explicitly allowed by a subcomponent -- except for the Upload component
  useEffect(() => {
    const disableDragAndDrop = (event) => {
      let target = event.target;
      while (target && target !== document) {
        if (target.classList && target.classList.contains('ant-upload')) {
          return;
        }
        target = target.parentNode;
      }
      event.preventDefault();
      event.dataTransfer.effectAllowed = 'none';
      event.dataTransfer.dropEffect = 'none';
    };
    // Adding document level event listeners
    document.addEventListener('dragover', disableDragAndDrop);
    document.addEventListener('drop', disableDragAndDrop);
    // Cleanup document level event listeners
    return () => {
      document.removeEventListener('dragover', disableDragAndDrop);
      document.removeEventListener('drop', disableDragAndDrop);
    };
  }, []);

  const getOrganizationMenuItems = () => {
    return [
      {
        key: 0,
        label: <span className="block text-black font-bold cursor-default">Organizations:</span>,
        disabled: true,
      },
      {
        type: 'divider',
      },
      ...(user.Organizations.sort((a, b) => a?.name?.localeCompare(b?.name)).map(org => ({
        key: org.organizationId,
        label: org.name,
        onClick: () => {
          setOrganizationId(org.organizationId);
          navigate(prefixRelativeOrg(org.urlSlug));
          setMenuIsOpen(false);
        },
      })))
    ];
  }

  const Analytics = useCallback(() => (
    <>
      <OpenReplayTracker
        projectKey={process.env.REACT_APP_OPENREPLAY_KEY}
        userId={user?.email}
        disableSecureMode={process.env.REACT_APP_OPENREPLAY_DISABLE_SECURE_MODE == 'true'}
        metadata={{ ENVIRONMENT: process.env.REACT_APP_OPENREPLAY_ENVIRONMENT }} />

      <GoogleAnalyticsTracker id={process.env.REACT_APP_ADMIN_GA4_ID} />
    </>
  ), []);

  const MobileHeader = useCallback(({ organization, menuIsOpen, setMenuIsOpen }) => (
    <span className="desktop:hidden pl-4 pr-4 flex items-center cursor-pointer" onClick={() => setMenuIsOpen(!menuIsOpen)}>
      <span className="custom-text-a6b4d7"><MenuOutlined /></span>
      <span className="inline-block w-4"></span>
      <span className="custom-text-a6b4d7 text-md overflow-hidden truncate">{organization?.nick}</span>
    </span>
  ), []);

  const DesktopOrganizationSwitcher = useCallback(({ organization }) => (
    <Dropdown menu={{ items: getOrganizationMenuItems() }} placement="bottomLeft" trigger={['click']}>
      <a className="inline-block select-none" onClick={(e) => e.preventDefault()}>
        <span className="custom-text-a6b4d7 text-md">{organization?.name}</span>
        <DownOutlined className="custom-text-a6b4d7 ml-1" />
      </a>
    </Dropdown>
  ), []);

  const DesktopHeader = useCallback(({ organization, user }) => (
    <span className="hidden desktop:flex items-center">
      <span className="text-2xl"><Link className="text-white" to="/">HMS</Link></span>
      <span className="inline-block w-4"></span>
      <span className="border-r border-gray-500 h-8 mx-2"></span>
      <span className="inline-block w-4"></span>
      {user.Organizations.length > 1 &&
        <DesktopOrganizationSwitcher
          organization={organization}
        />
      }
      {user.Organizations.length <= 1 &&
        <span className="custom-text-a6b4d7 text-md">{organization?.name}</span>
      }
    </span>
  ), []);

  const CommonHeader = useCallback(({ isMobile, menuIsOpen, setMenuIsOpen, hasMultipleRoles, organization, user }) => (
    <Header className="fixed top-0 left-0 right-0 h-15 z-10 custom-bg-002C8C px-0 desktop:px-7">
      {isMobile ? <>
        <div className="grid grid-cols-[1fr_auto_1fr] desktop:flex">
          <div className="">
            <MobileHeader 
              menuIsOpen={menuIsOpen} 
              setMenuIsOpen={setMenuIsOpen} 
              organization={organization}
            />
          </div>
          <span className="flex-grow flex items-center justify-center">
            <RoleSelector />
          </span>
          <span className="truncate flex items-center justify-end gap-5 pr-2 desktop:pr-4">
            <RequiredActionsIcon />
            <UserMenu hideNameOnMobile={hasMultipleRoles} />
          </span>
        </div>
      </> : <>
        <div className="flex">
          <DesktopHeader 
            organization={organization} 
            user={user}
          />
          <span className="flex-grow"></span>
          <span className="truncate flex items-center gap-5">
            <RequiredActionsIcon />
            <RoleSelector />
            <UserMenu hideNameOnMobile={hasMultipleRoles} />
          </span>
        </div>
      </>}
    </Header>
  ), []);

  const MobileOrganizationSwitcher = useCallback(({ organization }) => (
    <div className="bg-gray-400 py-4 flex justify-center max-w-[100%]">
      <Dropdown menu={{ items: getOrganizationMenuItems() }} placement="bottomLeft" trigger={['click']}>
        <span className="flex">
          <span className="max-w-[200px] text-white text-md overflow-ellipsis truncate">
            {organization?.name}
          </span>
          <DownOutlined className="text-white ml-1" />
        </span>
      </Dropdown>
    </div>
  ), []);

  const MobileDrawer = useCallback(({ menuIsOpen, setMenuIsOpen, organization }) => (
    <Drawer
      placement="left"
      closable={true}
      open={menuIsOpen}
      onClose={() => setMenuIsOpen(false)}
      headerStyle={{ display: 'none' }}
      width={250}
      bodyStyle={{
        padding: 0,
        overflow: "scroll"
      }
      }
    >
      <Header className="fixed z-10 custom-bg-002C8C px-0 w-[250px]">
        <span className="desktop:hidden pl-4 pr-4 flex items-center cursor-pointer" onClick={() => setMenuIsOpen(false)}>
          <span className="text-white"><MenuOutlined /></span>
          <span className="inline-block w-4"></span>
          <span className="text-xl text-white">HMS</span>
        </span>
      </Header>

      <div className="flex flex-col text-white h-[calc(100vh-190px)] pt-[64px]">  { /* -190px is necessary on a real device - otherwise RulePacks are not visible */}

        {user.Organizations.length > 1 &&
          <MobileOrganizationSwitcher
            organization={organization}
          />
        }

        <Navbar mode={mode} setMenuIsOpen={setMenuIsOpen} />
      </div>
    </Drawer>
  ), []);

  return (
    <LocationHistory>
      <Layout className="selector-admin-layout min-h-screen">
        <ConfirmGlobal />

        <Analytics />

        <Notifications />

        <RouterScrollToTop />

        <CommonHeader 
          isMobile={isMobile} 
          menuIsOpen={menuIsOpen} 
          setMenuIsOpen={setMenuIsOpen} 
          hasMultipleRoles={hasMultipleRoles} 
          organization={organization} 
          user={user}
        />

        <RequiredActionsModal />

        {/* Page body */}
        <Layout>
          <div className="flex w-full pt-16">
            <Sider className={`hidden ${navbar ? 'desktop:block' : ''} site-layout-background`} width={200}>
              <div className="fixed min-h-[calc(100vh-70px)] max-h-fit w-[200px] bg-white">
                <div className="h-screen flex flex-col justify-between pb-[70px]">
                  <div className="flex flex-grow flex-col overflow-y-auto">
                    <Navbar mode={mode} setMenuIsOpen={setMenuIsOpen} />
                  </div>
                </div>
              </div>
            </Sider>
            <Layout>
              {props.children ? props.children : <Outlet />}
            </Layout>
          </div>
        </Layout>
      </Layout>

      {/* Mobile Drawer */}
      <MobileDrawer 
        menuIsOpen={menuIsOpen} 
        setMenuIsOpen={setMenuIsOpen} 
        organization={organization}
      />

    </LocationHistory>
  );
}
